import type { Root } from './prototypeTypes';
import { playerSelectors } from './mediaPlayerSelectors';
import { applyVideoBrowserChrome, restoreVideoBrowserChrome } from './videoBrowserChrome';

const PIP_RETURNING_CLASS = 'video-pip-returning';

/**
 * Owns the page-side video presentation state. PiP changes presentation only:
 * the mounted media and article tree remain in place while this surface moves
 * between visible, detached, and closed states.
 */
export function openVideoPresentationSurface(root: Root): void {
	const { surface, doc } = presentationSurface(root);
	doc.documentElement.classList.remove(PIP_RETURNING_CLASS);
	if (!surface) return;
	surface.classList.remove('pip-detached', 'pip-background', 'pip-returning', 'pip-docked');
	surface.classList.add('active');
	surface.removeAttribute('aria-hidden');
	applyVideoBrowserChrome(doc);
}

/** Closes the page surface and restores the pre-video browser chrome. */
export function closeVideoPresentationSurface(root: Root): void {
	const { surface, doc } = presentationSurface(root);
	surface?.classList.remove('active', 'pip-detached', 'pip-background', 'pip-returning', 'pip-docked', 'controls-visible', 'controls-idle');
	doc.documentElement.classList.remove(PIP_RETURNING_CLASS);
	surface?.removeAttribute('aria-hidden');
	restoreVideoBrowserChrome(doc);
}

/** Keeps the player in the display tree while system PiP owns its presentation. */
export function detachVideoPresentationSurface(root: Root): void {
	const { surface, doc } = presentationSurface(root);
	doc.documentElement.classList.remove(PIP_RETURNING_CLASS);
	if (!surface) return;
	surface.classList.add('pip-detached', 'pip-docked');
	surface.classList.remove('active', 'pip-background', 'pip-returning');
	surface.setAttribute('aria-hidden', 'true');
	restoreVideoBrowserChrome(doc);
}

/**
 * The entry dock exists only while the PiP request is in flight (the system
 * reads the inline rect as its animation anchor). Release it once the session
 * is issued so a later restore flight grows toward the full-screen surface
 * instead of shrinking toward the dock.
 */
export function undockVideoPresentationSurface(root: Root): void {
	presentationSurface(root).surface?.classList.remove('pip-docked');
}

/**
 * Navigation while system PiP remains active must retain its original
 * element/observer, but the fixed video surface must not cover the destination
 * view. Keep it mounted and move only this detached presentation behind it.
 */
export function backgroundDetachedVideoPresentationSurface(root: Root): void {
	const { surface } = presentationSurface(root);
	if (surface?.classList.contains('pip-detached')) surface.classList.add('pip-background');
}

/** Covers only WebKit's PiP → native-fullscreen return gap, never normal PiP browsing. */
export function showVideoPictureInPictureReturnSurface(root: Root): void {
	const { surface, doc } = presentationSurface(root);
	if (surface?.classList.contains('pip-detached')) doc.documentElement.classList.add(PIP_RETURNING_CLASS);
}

function presentationSurface(root: Root): { surface: HTMLElement | null; doc: Document } {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	return { surface: doc.getElementById(playerSelectors.video.container), doc };
}
